import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdexService } from 'app/idex.service';
import { Toast } from 'app/shared/toast/toast';
import { Observable } from 'rxjs';
import { FileUploader } from './file-uploader';
@Injectable({
  providedIn: 'root'
})
export class GoogleUploaderService {

   private chunkSize = 1048576;
   busy: boolean;
   pathFolder: string;
   access_token: string;
   oldFileList: any;

   constructor(private translate: TranslateService, private service: IdexService, private zone: NgZone, private toast: Toast, private fileUploader: FileUploader) { }

   private init(access_token, folderId, fileName, maxFileSize, duplicateItem): Observable<any> {
      return this.service.uploadFileToGoogleDrive(access_token, folderId, fileName, maxFileSize, duplicateItem?.id)
   }

   process(access_token, dataTransfer: DataTransfer, callback: any, pathFolder, oldFileList): void {
      this.busy = true;
      this.access_token = access_token;
      this.pathFolder = pathFolder;
      this.oldFileList = oldFileList;
      if (dataTransfer.items) {
         const item = dataTransfer.items[0];
         if (item.webkitGetAsEntry) {
            const list = [];
            const dataTransferItem = dataTransfer.items;
            for (let i = 0; i < dataTransferItem.length; i++) {
               list.push(dataTransferItem[i].webkitGetAsEntry());
            }
            this.fileUploader.getFilesAsync(list).then(files => {
               this.uploadFiles(access_token, files, callback);
            }, () => {
               this.uploadFiles(access_token, dataTransfer.files, callback);
            });
         } else {
            this.uploadFiles(access_token, dataTransfer.files, callback);
         }
      } else {
         this.uploadFiles(access_token, dataTransfer.files, callback);
      }
   }

   uploadFolder(access_token, list: FileList, callback: any, pathFolder): void {
      this.busy = true;
      this.pathFolder = pathFolder;
      this.access_token = access_token;
      const files = [];
      for (let i = 0; i < list.length; i++) {
         const file: any = list.item(i);
         file.fullPath = this.trimName(file.webkitRelativePath);
         files.push(file);
      }
      this.uploadFiles(access_token, files, callback);
   }

   trimName(fullPath: string): string | null {
      const resultArray: string[] = fullPath.split('/').map(item => item.trim());
      const mergedString: string = resultArray.join('/');
      return mergedString;
  }

   uploadFiles(access_token: string, files: any, callback: any): void {
      this.busy = true;
      this.access_token = access_token;
      if (files.length === 0) {
         this.busy = false;
         return;
      }
      this.zone.run(() => { callback.init(files); });
      let index = 0;
      let folderTree = [];
      const uploadNextFile = async () => {
         if (index === files.length) {
            this.busy = false;
            this.zone.run(() => { callback.complete(); });
            return;
         }
         const file: any = await this.uploadFolderProcess(folderTree, files[index++]);
         if (file.canceled) {
            this.zone.run(() => { callback.cancelUpload(index - 1, 'Cancel'); });
            uploadNextFile();
            return;
         }
         if (/[/\\:*?"<>|]/.test(file.name)) {
            this.translate.get('alert.create_folder_error').subscribe(s=>{
               this.toast.error(s);});
            this.zone.run(() => { callback.specialCharacter(index - 1); });
            uploadNextFile();
            return;
         }
         this.init(access_token, file.uploadFolderId, file.name, file.size, file.duplicateItem).subscribe(result => {
            let offset = 0;
            const xhr = new XMLHttpRequest();
            const send = () => {
               const end = Math.min(offset + this.chunkSize, file.size);
               xhr.open('PUT', this.service.url + '/googledrive/upload/chunk/' + (result.message ? result.message.split('resumable&')[1] : null), true);
               xhr.setRequestHeader('x-auth-token', this.service.user.token);
               xhr.setRequestHeader('Content-Range', 'bytes ' + offset + '-' + (end - 1) + '/' + file.size);
               const blob = file.slice.call(file, offset, end);
               xhr.send(blob);
            };
            xhr.onload = () => {
               if (xhr.status === 308) {
                  if (file.canceled) {
                     this.zone.run(() => {
                        callback.cancelUpload(index - 1, 'Cancel');
                     });
                     uploadNextFile();
                     return;
                  }
                  const progress = Math.floor(offset * 100 / file.size);
                  this.zone.run(() => { callback.progress(index - 1, progress); });
                  offset += this.chunkSize;
                  send();
               } else if (xhr.status === 200 || xhr.status === 201) {
                  this.zone.run(() => { callback.progress(index - 1, 100); });
                  uploadNextFile();
               } else if (xhr.status === 403) {
                  this.zone.run(() => { callback.quotaExceed(index - 1, file.size, "Rate Limit Exceeded"); });
                  uploadNextFile();
               } else {
                  file.canceled = true;
                  this.zone.run(() => { callback.cancelUpload(index - 1, 'Service Unavailable'); });
                  uploadNextFile();
               }
            };
            send();
         }, error => {
            file.error = error;
            this.zone.run(() => { callback.cancelUpload(index - 1, error); });
            uploadNextFile();
         });
      };
      uploadNextFile();
   }

   async createFolderStructure(folderNames: string[], parentId, nestedFolder, folderTree): Promise<string> {
      let currentParentId = parentId;
      let i = 0;
      for (const folderName of folderNames) {
         if(folderTree.length > 0){
            let folderTreeIndex = folderTree.findIndex(item => item[i]?.name === folderName);
            if(folderTreeIndex >= 0){
               currentParentId = folderTree[folderTreeIndex][i].id;
               nestedFolder.push({name: folderName, id: currentParentId});
               i++;
               continue;
            } else {
               currentParentId = await this.createOrFindFolder(folderName, currentParentId);
               nestedFolder.push({name: folderName, id: currentParentId});
               i++;
            }
         } else {
            currentParentId = await this.createOrFindFolder(folderName, currentParentId);
            nestedFolder.push({name: folderName, id: currentParentId});
         }
      }

      return currentParentId;
   }

   async createOrFindFolder(folderName: string, parentId: string): Promise<string> {
      let response = await this.service.getFolderFromGoogleDrive(this.access_token, folderName, parentId).toPromise();
      if (response.files.length > 0) {
         for (const folder of response.files) {
            if (folder.name === folderName) {
               return folder.id;
            }
         }
      }
      let createResponse = await this.service.createFolderInGoogleDrive(this.access_token, parentId, folderName).toPromise();
      return createResponse.id;
   }

   async uploadFolderProcess(folderTree, file): Promise<any> {
      if(!this.pathFolder){
         return this.changeDuplicateFileName(file, this.oldFileList);
      }
      let nestedFolder = [];
      let folderNameArray = file.webkitRelativePath ? file.webkitRelativePath.split('/') : file.fullPath.split('/');
      if (folderNameArray[0] == ''){
         folderNameArray.pop();
         folderNameArray.shift();
         if(folderNameArray.length === 0){
            file = this.changeDuplicateFileName(file, this.oldFileList);
         }
      } else {
         folderNameArray.pop();
      }
      const lastFolderId = await this.createFolderStructure(folderNameArray, this.pathFolder, nestedFolder, folderTree);
      file.uploadFolderId = lastFolderId;
      let resItemList = await this.service.getFilesFromGoogleDrive(this.access_token, lastFolderId).toPromise();
      file.duplicateItem = resItemList.files?.find(item => item.name === file.name);
      folderTree.push(nestedFolder);
      return file;
   }

   changeDuplicateFileName(uploadFile:File, duplicateFile:any[]): File {
      let counter = 1;
      let originalName = uploadFile.name;
      let newName = originalName;

      while (duplicateFile?.find(file => file.name === newName)) {
         newName = `${originalName.split('.')[0]} (${counter}).${originalName.split('.')[1]}`;
         counter++;
      }

      if (newName !== originalName) {
         Object.defineProperty(uploadFile, 'name', {
               writable: true,
               value: newName
         });
      }
      return uploadFile;
   };
}
